import { useCallback } from 'react';
import { useParams } from '@remix-run/react';
import { useAtom } from 'jotai';
import { Article } from 'shared/models/legacy';
import { $PoiPost, $PoiPostLoadmore } from 'shared/atoms';
import { ArticleCard } from '~/components/article-card';
import { EmptyPosts } from '~/components/empty-posts';
import { SeeMore } from '~/components/see-more';
import { DivisibleStack } from '~/components/divisible-stack';
import { ModuleType } from '~/utils/module-type';
import { ViewTracker } from '~/components/view-tracker';
import { Methods } from '@poi/constant';
import { $CurrentPageModule } from '~/models/current-page-module';
import { ScopedProvider, useRouteLoader } from '@lemon8/web-app-shared/ssr';
import { ClickPositionPrefix } from '~/utils/click-position';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';

import './style.scss';

export default function PoiPostSection(): JSX.Element | null {
  const { poi_id: poiId = '' } = useParams<{ poi_id: string }>();

  const [poiPostData] = useAtom($PoiPost(poiId));

  const { items: poiPosts = [], hasMore = false, sessionImprId = '' } = poiPostData;

  const loader = useRouteLoader(
    Methods.PoiPostLoadMore,
    {
      session_impr_id: sessionImprId,
    },
    {
      atomFamilies: {
        $PoiPostLoadmore,
      },
    },
  );

  const handleSeeMore = (): void => {
    if (loader.state === 'loading') {
      return;
    }

    loader.load();
  };

  const sendTrackingEvent = useCallback(() => {
    window.Tea('wap_module_show', {
      current_page_module: ModuleType.related_article,
    });
  }, []);

  return (
    <ViewTracker onView={sendTrackingEvent}>
      <section id="poi-post-section">
        {poiPostData.items.length > 0 ? (
          <ScopedProvider
            atomsWithHydration={[
              [$CurrentPageModule, ModuleType.related_article],
              [$CurrentPositionPrefix, ClickPositionPrefix.none],
            ]}
          >
            <DivisibleStack wrapper="article-list" divider="article-list-divider">
              {/* @ts-ignore */}
              {poiPosts.map((a: Article, rank) => (
                <ArticleCard article={a} key={a.groupId} rank={rank} />
              ))}
            </DivisibleStack>
            {hasMore && <SeeMore onClickSeeMore={handleSeeMore} loading={loader.state === 'loading'} />}
          </ScopedProvider>
        ) : (
          <section className="poi-post-section">
            <EmptyPosts />
          </section>
        )}
      </section>
    </ViewTracker>
  );
}
